<template>
  <div class="pdf-viewer">
    <div class="pages">
      <ul class="page-list">
        <li v-for="pageNumber in totalPages" :key="pageNumber" @click="scrollToPage(pageNumber)">
          <canvas class="thumbnail"></canvas>
          <span>{{ pageNumber }}</span>
        </li>
      </ul>
    </div>

    <div class="canvas-container gap-1">
      <div v-for="pageNumber in totalPages" :key="pageNumber" class="pdfViewer singlePageView"
        :style="{ '--scale-factor': scaleByPage[pageNumber] }">
      
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import * as pdfjsViewer from 'pdfjs-dist/web/pdf_viewer.mjs';

import "pdfjs-dist/web/pdf_viewer.css"
import store from '@/store';

const props = defineProps({
  pdfUrl: {
    type: String,
    required: true,
  },
});

GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js?bypasscache';

const scaleByPage = ref({});
const totalPages = ref(0);

async function renderPage(page, eventBus, pageNumber) {
  const container = document.querySelector(`.canvas-container .pdfViewer:nth-child(${pageNumber})`);

  const viewportAtScale1 = page.getViewport({ scale: 1 });
  const pageWidthInPdfUnits = viewportAtScale1.width;

  let desiredWidthInPixels;
  if (store.state.system.mobileMode) {
    desiredWidthInPixels = window.innerWidth - 10;
  } else {
    desiredWidthInPixels = Math.min(window.innerWidth - 400, window.innerWidth * 0.6);
  }
  
  scaleByPage.value[pageNumber] = desiredWidthInPixels / pageWidthInPdfUnits;
  const pdfPageView = new pdfjsViewer.PDFPageView({
    container,
    id: `pageCanvas_${pageNumber}`,
    scale: scaleByPage.value[pageNumber],
    defaultViewport: page.getViewport({ scale: scaleByPage.value[pageNumber] }),
    eventBus,
  });
  // Associate the actual page with the view, and draw it.
  pdfPageView.setPdfPage(page);
  
  pdfPageView.draw();

    
}

async function renderThumbnail(page, pageNumber) {
  const canvas = document.querySelector(`.pages .page-list li:nth-child(${pageNumber}) canvas`);
  const viewport = page.getViewport({ scale: 0.4 });
  const context = canvas.getContext('2d');
  canvas.height = viewport.height;
  canvas.width = viewport.width;

  const renderContext = {
    canvasContext: context,
    viewport: viewport,
  };

  await page.render(renderContext).promise;
}

async function scrollToPage(pageNumber) {
  const container = document.querySelector('.canvas-container');
  const pageElement = container.querySelector(`.pdfViewer:nth-child(${pageNumber})`);
  if (pageElement) {
    pageElement.scrollIntoView({ behavior: 'smooth' });
  }
}

async function getAndRender(pdf, eventBus, pageNumber) {
  const page = await pdf.getPage(pageNumber);
  renderPage(page, eventBus, pageNumber);
  renderThumbnail(page, pageNumber);
}


onMounted(async () => {
  const eventBus = new pdfjsViewer.EventBus();
  const loadingTask = getDocument({ url: props.pdfUrl });
  const pdf = await loadingTask.promise;
  totalPages.value = pdf.numPages;

  for (let pageNumber = 1; pageNumber <= totalPages.value; pageNumber++) {
    getAndRender(pdf, eventBus, pageNumber);
  }

});
</script>

<style scoped lang="scss">
.pdf-viewer {
  display: flex;
  flex-direction: row;
  height: 100%;
}

.singlePageView {
  margin: 0 auto; // here it is better to use margin auto then flex align items for case when the horisontal scroll appears 
}

.pages {
  width: 200px;
  overflow-y: auto;
  background-color: var(--card-background);
  border-right: 1px solid var(--card-border);
  @media screen and (max-width: 600px) {
    display: none;
  }
}

.page-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.page-list li {
  padding: 10px;
  cursor: pointer;
  border-bottom: 1px solid var(--card-border);
  display: flex;
  flex-direction: column;
  color: var(--text);
  align-items: center;
  gap: 10px;
}

.page-list li:hover {
  background-color: var(--card-hover);
}

.thumbnail {
  width: 150px;
  height: auto;
}

.canvas-container {
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
}

.page-canvas {
  padding: 0 50px;
  position: relative;
  @media screen and (max-width: 600px) {
    padding: 0 5px;
  }
  canvas {
    max-width: 100%;
  }
}

.text-layer {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  pointer-events: none;
}
</style>